import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BookOpen } from 'lucide-react';
import api from '../../../../services/api';
import useQuizStore from '../../../../store/quizStore';
import Quiz from '../../Quiz';
import { NotionReader, NotionRevoir } from '../TabNotions';
import { scoreColor, timeOfDay, nbspFr } from './helpers';
import DayTimeline from './DayTimeline';

/**
 * Section « Notions » de l'onglet Résultats (comme « Réponses rédigées ») : une ligne par séance de test d'une
 * notion de cette thématique (notions/history.php). Ces séances tournent dans la thématique technique « notions »,
 * hors de l'historique de la thématique : sans cette section, on ne les retrouvait que dans la fenêtre de la notion.
 * « Consulter » rouvre la séance en lecture (quizStore.startReview, dans la thématique technique).
 * Masquée tant qu'aucune séance n'existe. `reloadKey` : recharge (fin de quiz, effacement d'historique…).
 */
export default function NotionHistoryView({ scopeInfo, reloadKey }) {
    const scopeId = scopeInfo?.id;
    const [items, setItems] = useState(null);
    const [review, setReview] = useState(null); // séance en cours de consultation
    const [detail, setDetail] = useState(null);  // fiche de la notion consultée (parties, rattachement des questions)
    const [readerOver, setReaderOver] = useState(null); // partie ouverte par-dessus la relecture

    useEffect(() => {
        if (!scopeId) return undefined;
        let alive = true;
        api.get('/notions/history.php', { params: { scope_id: scopeId } })
            .then(({ data }) => { if (alive) setItems(data.data?.items || []); })
            .catch(() => { if (alive) setItems([]); });
        return () => { alive = false; };
    }, [scopeId, reloadKey]);

    const consulter = async (it) => {
        // La fiche donne « Revoir dans la fiche » sous chaque explication, comme dans la fenêtre de la notion.
        const [, det] = await Promise.all([
            useQuizStore.getState().startReview(it, it.scope_id),
            api.get('/notions/detail.php', { params: { slug: it.notion.slug, scope_id: scopeId } }).then(r => r.data.data).catch(() => null),
        ]);
        setDetail(det);
        setReview(it);
    };
    const closeReview = () => { useQuizStore.getState().reset(); setReview(null); setDetail(null); setReaderOver(null); };
    const overlayBg = detail?.matiere_image ? { backgroundImage: `url(${detail.matiere_image})` } : {};

    if (!items || items.length === 0) return null;

    return (
        <div className="qrc-hist notion-hist">
            <div className="qrc-hist__head">
                <h3 className="sl-section-title"><BookOpen size={16} /> Notions</h3>
                <p className="sl-section-sub">
                    {items.length} séance{items.length > 1 ? 's' : ''} de test sur les notions — chaque séance reste consultable.
                </p>
            </div>
            <DayTimeline items={items} dateOf={it => it.created_at} keyOf={it => it.id}
                pctOf={it => (it.total ? Math.round(it.score / it.total * 100) : 0)}
                unit={n => `${n} séance${n > 1 ? 's' : ''}`}
                renderCard={it => {
                    const pct = it.total ? Math.round(it.score / it.total * 100) : 0;
                    return (
                        <div key={it.id} className="hist-card">
                            <span className="hist-card__expand" />
                            <div className="hist-card__main">
                                <div className="hist-card__titleline">
                                    <span className="hist-card__title hist-card__title--clamp" title={it.notion.titre}>{nbspFr(it.notion.titre)}</span>
                                </div>
                                <div className="hist-card__meta">
                                    {timeOfDay(it.created_at)} · {it.mode === 'apprentissage' ? 'Apprentissage' : 'Classique'}
                                </div>
                            </div>
                            <div className="hist-card__score">
                                <span className="hist-card__pct" style={{ color: scoreColor(pct) }}>{pct}%</span>
                                <span className="hist-card__frac">{it.score}/{it.total}</span>
                            </div>
                            <div className="hist-card__actions">
                                <button type="button" className="hist-btn hist-btn--accent" onClick={() => consulter(it)}>Consulter</button>
                            </div>
                        </div>
                    );
                }} />
            {review && createPortal(
                <>
                    <div className={`quiz-modal-overlay ${overlayBg.backgroundImage ? 'has-bg-image' : ''}`} style={overlayBg}>
                        <Quiz sousSujetIdProp={review.sous_sujet_id} scopeIdProp={review.scope_id} scopeInfoProp={scopeInfo}
                            nbQuestionsProp={null} modeProp={null} originProp="subject"
                            afterExplanation={detail ? (q) => <NotionRevoir d={detail} question={q} onOpen={setReaderOver} /> : undefined}
                            onClose={closeReview} />
                    </div>
                    {detail && readerOver !== null && (
                        <NotionReader d={detail} start={readerOver} scopeInfo={scopeInfo} overlayBg={overlayBg} onTop
                            onRead={(read_sections, read) => setDetail(x => ({ ...x, read_sections, read: read || x.read }))}
                            onClose={() => setReaderOver(null)} />
                    )}
                </>,
                document.body
            )}
        </div>
    );
}
